import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getTeacherStats,
  getBoards, createBoard, updateBoard, deleteBoard,
  getClasses, createClass, updateClass, deleteClassItem,
  getSubjects, createSubject, updateSubject, deleteSubject,
  getNotes, uploadNote, deleteNote,
  getVideos, uploadVideo, deleteVideo,
} from "../api";

const TABS = ["Overview", "Boards & Classes", "Subjects", "Upload Note", "Upload Video", "Manage Content"];
const TAB_ICONS = {
  "Overview": "📊", "Boards & Classes": "🏫", "Subjects": "📚",
  "Upload Note": "📄", "Upload Video": "🎥", "Manage Content": "⚙",
};

export default function TeacherDashboard() {
  const { user, logout, isTeacher } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");

  if (!user || !isTeacher) {
    return (
      <div style={s.notAuth}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={s.naTitle}>Access Restricted</div>
        <div style={s.naSub}>You need to be logged in as a teacher.</div>
        <button style={s.naBtn} onClick={() => navigate("/login")}>Go to Login</button>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.greeting}>Welcome back, {user.name} 👋</div>
          <div style={s.role}>Admin / Teacher Dashboard</div>
        </div>
        <button style={s.logoutBtn} onClick={async () => { await logout(); navigate("/"); }}>
          Logout
        </button>
      </div>

      <div style={s.tabs}>
        {TABS.map((t) => (
          <button key={t} style={{ ...s.tab, ...(tab === t ? s.tabActive : {}) }} onClick={() => setTab(t)}>
            {TAB_ICONS[t]} {t}
          </button>
        ))}
      </div>

      {tab === "Overview"         && <Overview />}
      {tab === "Boards & Classes" && <ManageBoardsClasses />}
      {tab === "Subjects"         && <ManageSubjects />}
      {tab === "Upload Note"      && <UploadNote />}
      {tab === "Upload Video"     && <UploadVideo />}
      {tab === "Manage Content"   && <ManageContent />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   OVERVIEW — live stats from Spring Boot
───────────────────────────────────────────── */
function Overview() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTeacherStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  const cards = stats ? [
    { label: "Total Notes",    value: stats.totalNotes,    icon: "📄", color: "#3b82f6" },
    { label: "Total Videos",   value: stats.totalVideos,   icon: "🎥", color: "#8b5cf6" },
    { label: "Total Boards",   value: stats.totalBoards,   icon: "🏫", color: "#10b981" },
    { label: "Total Classes",  value: stats.totalClasses,  icon: "📋", color: "#f59e0b" },
    { label: "Total Subjects", value: stats.totalSubjects, icon: "📚", color: "#ec4899" },
  ] : [];

  if (loading) return <div style={s.loadMsg}>Loading stats…</div>;

  return (
    <div>
      <div style={s.statsGrid}>
        {cards.map((c) => (
          <div key={c.label} style={s.statCard}>
            <div style={{ ...s.statIcon, background: c.color + "22" }}>{c.icon}</div>
            <div style={s.statVal}>{c.value}</div>
            <div style={s.statLabel}>{c.label}</div>
          </div>
        ))}
      </div>
      {!stats && <div style={s.errMsg}>Could not load stats from server.</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MANAGE BOARDS & CLASSES — real API
───────────────────────────────────────────── */
function ManageBoardsClasses() {
  const [boards, setBoards]           = useState([]);
  const [classes, setClasses]         = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [newBoardName, setNewBoardName] = useState("");
  const [newBoardSlug, setNewBoardSlug] = useState("");
  const [editingBoard, setEditingBoard] = useState(null);
  const [boardMsg, setBoardMsg]         = useState({ text: "", type: "ok" });
  const [newClassName, setNewClassName] = useState("");
  const [newClassNum, setNewClassNum]   = useState("");
  const [editingClass, setEditingClass] = useState(null);
  const [classMsg, setClassMsg]         = useState({ text: "", type: "ok" });
  const [boardLoading, setBoardLoading] = useState(false);
  const [classLoading, setClassLoading] = useState(false);

  useEffect(() => {
    getBoards().then(setBoards).catch(() => setBoards([]));
  }, []);

  useEffect(() => {
    if (!selectedBoard) { setClasses([]); return; }
    getClasses(selectedBoard).then(setClasses).catch(() => setClasses([]));
  }, [selectedBoard]);

  const flash = (setter, text, type = "ok") => {
    setter({ text, type });
    setTimeout(() => setter({ text: "", type: "ok" }), 3000);
  };

  // Board ops
  const addBoard = async () => {
    if (!newBoardName.trim()) return;
    setBoardLoading(true);
    try {
      const slug = newBoardSlug.trim() || newBoardName.toLowerCase().replace(/\s+/g, "-");
      const created = await createBoard({ name: newBoardName.trim(), slug });
      setBoards((p) => [...p, created]);
      setNewBoardName(""); setNewBoardSlug("");
      flash(setBoardMsg, "✅ Board added!");
    } catch (e) {
      flash(setBoardMsg, "❌ " + (e?.response?.data?.message || "Failed"), "err");
    } finally { setBoardLoading(false); }
  };

  const saveBoard = async () => {
    if (!editingBoard?.name.trim()) return;
    setBoardLoading(true);
    try {
      const updated = await updateBoard(editingBoard.id, { name: editingBoard.name, slug: editingBoard.slug });
      setBoards((p) => p.map((b) => b.id === updated.id ? updated : b));
      setEditingBoard(null);
      flash(setBoardMsg, "✅ Board updated!");
    } catch (e) {
      flash(setBoardMsg, "❌ " + (e?.response?.data?.message || "Failed"), "err");
    } finally { setBoardLoading(false); }
  };

  const removeBoard = async (id) => {
    if (!window.confirm("Delete this board and all its classes?")) return;
    try {
      await deleteBoard(id);
      setBoards((p) => p.filter((b) => b.id !== id));
      if (selectedBoard === id) setSelectedBoard(null);
      flash(setBoardMsg, "✅ Board deleted!");
    } catch (e) {
      flash(setBoardMsg, "❌ " + (e?.response?.data?.message || "Failed"), "err");
    }
  };

  // Class ops
  const addClassItem = async () => {
    if (!newClassName.trim() || !selectedBoard) return;
    setClassLoading(true);
    try {
      const created = await createClass({ name: newClassName.trim(), number: Number(newClassNum) || 0, boardId: selectedBoard });
      setClasses((p) => [...p, created]);
      setNewClassName(""); setNewClassNum("");
      flash(setClassMsg, "✅ Class added!");
    } catch (e) {
      flash(setClassMsg, "❌ " + (e?.response?.data?.message || "Failed"), "err");
    } finally { setClassLoading(false); }
  };

  const saveClass = async () => {
    if (!editingClass?.name.trim()) return;
    setClassLoading(true);
    try {
      const updated = await updateClass(editingClass.id, { name: editingClass.name, number: editingClass.number, boardId: selectedBoard });
      setClasses((p) => p.map((c) => c.id === updated.id ? updated : c));
      setEditingClass(null);
      flash(setClassMsg, "✅ Class updated!");
    } catch (e) {
      flash(setClassMsg, "❌ " + (e?.response?.data?.message || "Failed"), "err");
    } finally { setClassLoading(false); }
  };

  const removeClass = async (id) => {
    if (!window.confirm("Delete this class?")) return;
    try {
      await deleteClassItem(id);
      setClasses((p) => p.filter((c) => c.id !== id));
      flash(setClassMsg, "✅ Class deleted!");
    } catch (e) {
      flash(setClassMsg, "❌ " + (e?.response?.data?.message || "Failed"), "err");
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      {/* Boards */}
      <div style={s.panel}>
        <div style={s.panelHeader}>
          <span style={s.panelTitle}>🏫 Boards</span>
          <span style={s.panelCount}>{boards.length} boards</span>
        </div>
        {boardMsg.text && <div style={{ ...s.flashMsg, ...(boardMsg.type === "err" ? s.flashErr : {}) }}>{boardMsg.text}</div>}
        <div style={s.inlineForm}>
          {editingBoard ? (
            <>
              <input style={s.inlineInput} value={editingBoard.name} onChange={(e) => setEditingBoard((p) => ({ ...p, name: e.target.value }))} placeholder="Board name" />
              <input style={s.inlineInput} value={editingBoard.slug} onChange={(e) => setEditingBoard((p) => ({ ...p, slug: e.target.value }))} placeholder="slug" />
              <div style={{ display: "flex", gap: 6 }}>
                <button style={s.saveBtn} onClick={saveBoard} disabled={boardLoading}>Save</button>
                <button style={s.cancelBtn} onClick={() => setEditingBoard(null)}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <input style={s.inlineInput} value={newBoardName} onChange={(e) => setNewBoardName(e.target.value)} placeholder="Board name (e.g. CBSE)" />
              <input style={s.inlineInput} value={newBoardSlug} onChange={(e) => setNewBoardSlug(e.target.value)} placeholder="Slug (auto if blank)" />
              <button style={s.addBtn} onClick={addBoard} disabled={boardLoading}>+ Add</button>
            </>
          )}
        </div>
        <div style={s.itemList}>
          {boards.map((b) => (
            <div key={b.id} style={{ ...s.itemRow, ...(selectedBoard === b.id ? s.itemRowActive : {}) }}
              onClick={() => setSelectedBoard(b.id === selectedBoard ? null : b.id)}>
              <div style={s.itemIcon}>🏫</div>
              <div style={s.itemInfo}>
                <div style={s.itemName}>{b.name}</div>
                <div style={s.itemSub}>/{b.slug}</div>
              </div>
              <div style={s.itemActions} onClick={(e) => e.stopPropagation()}>
                <button style={s.editBtn} onClick={() => setEditingBoard({ ...b })}>✏</button>
                <button style={s.delBtn} onClick={() => removeBoard(b.id)}>🗑</button>
              </div>
            </div>
          ))}
          {boards.length === 0 && <div style={s.noItems}>No boards yet.</div>}
        </div>
      </div>

      {/* Classes */}
      <div style={s.panel}>
        <div style={s.panelHeader}>
          <span style={s.panelTitle}>📋 Classes</span>
          <span style={s.panelCount}>{classes.length} classes</span>
        </div>
        {!selectedBoard && <div style={s.hintBox}>👆 Click a board on the left to manage its classes</div>}
        {classMsg.text && <div style={{ ...s.flashMsg, ...(classMsg.type === "err" ? s.flashErr : {}) }}>{classMsg.text}</div>}
        {selectedBoard && (
          <div style={s.inlineForm}>
            {editingClass ? (
              <>
                <input style={s.inlineInput} value={editingClass.name} onChange={(e) => setEditingClass((p) => ({ ...p, name: e.target.value }))} placeholder="Class name" />
                <input style={{ ...s.inlineInput, width: 70 }} type="number" value={editingClass.number} onChange={(e) => setEditingClass((p) => ({ ...p, number: Number(e.target.value) }))} placeholder="No." />
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={s.saveBtn} onClick={saveClass} disabled={classLoading}>Save</button>
                  <button style={s.cancelBtn} onClick={() => setEditingClass(null)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <input style={s.inlineInput} value={newClassName} onChange={(e) => setNewClassName(e.target.value)} placeholder="Class name (e.g. Class 10)" />
                <input style={{ ...s.inlineInput, width: 70 }} type="number" value={newClassNum} onChange={(e) => setNewClassNum(e.target.value)} placeholder="No." />
                <button style={s.addBtn} onClick={addClassItem} disabled={classLoading}>+ Add</button>
              </>
            )}
          </div>
        )}
        <div style={s.itemList}>
          {classes.map((c) => (
            <div key={c.id} style={s.itemRow}>
              <div style={{ ...s.itemIcon, background: "#0d2d1a", color: "#34d399", fontSize: 13, fontWeight: 700, fontFamily: "monospace" }}>{c.number || "–"}</div>
              <div style={s.itemInfo}>
                <div style={s.itemName}>{c.name}</div>
                <div style={s.itemSub}>{c.boardName}</div>
              </div>
              <div style={s.itemActions}>
                <button style={s.editBtn} onClick={() => setEditingClass({ ...c })}>✏</button>
                <button style={s.delBtn} onClick={() => removeClass(c.id)}>🗑</button>
              </div>
            </div>
          ))}
          {selectedBoard && classes.length === 0 && <div style={s.noItems}>No classes yet. Add one above.</div>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MANAGE SUBJECTS — real API
───────────────────────────────────────────── */
function ManageSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards]     = useState([]);
  const [classes, setClasses]   = useState([]);
  const [newName, setNewName]   = useState("");
  const [newIcon, setNewIcon]   = useState("");
  const [newBoardId, setNewBoardId] = useState("");
  const [newClassId, setNewClassId] = useState("");
  const [editing, setEditing]   = useState(null);
  const [msg, setMsg]           = useState({ text: "", type: "ok" });
  const [loading, setLoading]   = useState(false);

  const ICONS = ["📐","🔬","📖","🌍","🪔","⚛️","🧪","🧬","🖥️","🎨","🎵","🏃","💻","🌱","🧮"];

  useEffect(() => {
    getBoards().then(setBoards).catch(() => {});
    getSubjects().then(setSubjects).catch(() => setSubjects([]));
  }, []);

  useEffect(() => {
    if (newBoardId) getClasses(newBoardId).then(setClasses).catch(() => setClasses([]));
    else setClasses([]);
  }, [newBoardId]);

  const flash = (text, type = "ok") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "", type: "ok" }), 3000); };

  const add = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      const created = await createSubject({ name: newName.trim(), icon: newIcon || "📘", boardId: newBoardId || null, classId: newClassId || null });
      setSubjects((p) => [...p, created]);
      setNewName(""); setNewIcon(""); setNewBoardId(""); setNewClassId("");
      flash("✅ Subject added!");
    } catch (e) {
      flash("❌ " + (e?.response?.data?.message || "Failed"), "err");
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!editing?.name.trim()) return;
    setLoading(true);
    try {
      const updated = await updateSubject(editing.id, { name: editing.name, icon: editing.icon });
      setSubjects((p) => p.map((s) => s.id === updated.id ? updated : s));
      setEditing(null);
      flash("✅ Subject updated!");
    } catch (e) {
      flash("❌ " + (e?.response?.data?.message || "Failed"), "err");
    } finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this subject?")) return;
    try {
      await deleteSubject(id);
      setSubjects((p) => p.filter((s) => s.id !== id));
      flash("✅ Subject deleted!");
    } catch (e) {
      flash("❌ " + (e?.response?.data?.message || "Failed"), "err");
    }
  };

  return (
    <div style={s.panel}>
      <div style={s.panelHeader}>
        <span style={s.panelTitle}>📚 Subjects</span>
        <span style={s.panelCount}>{subjects.length} subjects</span>
      </div>
      {msg.text && <div style={{ ...s.flashMsg, ...(msg.type === "err" ? s.flashErr : {}) }}>{msg.text}</div>}
      <div style={s.subjectForm}>
        {editing ? (
          <>
            <input style={s.inlineInput} value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} placeholder="Subject name" />
            <input style={{ ...s.inlineInput, width: 70 }} value={editing.icon} onChange={(e) => setEditing((p) => ({ ...p, icon: e.target.value }))} placeholder="Icon" />
            <div style={{ display: "flex", gap: 6 }}>
              <button style={s.saveBtn} onClick={save} disabled={loading}>Save</button>
              <button style={s.cancelBtn} onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <input style={s.inlineInput} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Subject name" />
            <input style={{ ...s.inlineInput, width: 60 }} value={newIcon} onChange={(e) => setNewIcon(e.target.value)} placeholder="Icon" />
            <select style={{ ...s.inlineInput, width: 130 }} value={newBoardId} onChange={(e) => setNewBoardId(e.target.value)}>
              <option value="">Board (opt)</option>
              {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select style={{ ...s.inlineInput, width: 130 }} value={newClassId} onChange={(e) => setNewClassId(e.target.value)}>
              <option value="">Class (opt)</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button style={s.addBtn} onClick={add} disabled={loading}>+ Add</button>
          </>
        )}
      </div>
      <div style={s.iconPicker}>
        <span style={s.iconPickerLabel}>Quick icons:</span>
        {ICONS.map((ic) => (
          <button key={ic} style={s.iconChip} onClick={() => editing ? setEditing((p) => ({ ...p, icon: ic })) : setNewIcon(ic)}>{ic}</button>
        ))}
      </div>
      <div style={s.subjectGrid}>
        {subjects.map((sub) => (
          <div key={sub.id} style={s.subjectCard}>
            <div style={s.subjectIcon}>{sub.icon}</div>
            <div style={s.subjectName}>{sub.name}</div>
            <div style={s.itemActions}>
              <button style={s.editBtn} onClick={() => setEditing({ ...sub })}>✏</button>
              <button style={s.delBtn} onClick={() => del(sub.id)}>🗑</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   UPLOAD NOTE — real API with multipart
───────────────────────────────────────────── */
function UploadNote() {
  const [boards, setBoards]     = useState([]);
  const [classes, setClasses]   = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm]         = useState({ title: "", description: "", boardId: "", classId: "", subjectId: "", file: null });
  const [success, setSuccess]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => { getBoards().then(setBoards).catch(() => {}); }, []);
  useEffect(() => {
    if (form.boardId) getClasses(form.boardId).then(setClasses).catch(() => setClasses([]));
    else setClasses([]);
  }, [form.boardId]);
  useEffect(() => {
    if (form.boardId && form.classId) getSubjects(form.boardId, form.classId).then(setSubjects).catch(() => setSubjects([]));
    else setSubjects([]);
  }, [form.boardId, form.classId]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.subjectId) { setError("Title and Subject are required."); return; }
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("subjectId", form.subjectId);
      if (form.file) fd.append("file", form.file);
      await uploadNote(fd);
      setSuccess(true);
      setForm({ title: "", description: "", boardId: "", classId: "", subjectId: "", file: null });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || "Upload failed");
    } finally { setLoading(false); }
  };

  return (
    <div style={s.formCard}>
      <div style={s.formTitle}>📄 Upload New Note</div>
      {success && <div style={s.successBanner}>✅ Note uploaded successfully!</div>}
      {error   && <div style={s.errorBanner}>❌ {error}</div>}
      <form onSubmit={handleSubmit} style={s.form}>
        <div style={s.formGrid}>
          <Field label="Title *"      value={form.title}       onChange={(v) => set("title", v)}       placeholder="Chapter 1 – Real Numbers" />
          <Field label="Description"  value={form.description} onChange={(v) => set("description", v)} placeholder="Brief description" />
          <SelectField label="Board"   value={form.boardId}   onChange={(v) => { set("boardId", v); set("classId", ""); set("subjectId", ""); }} options={boards.map((b) => ({ value: b.id, label: b.name }))} />
          <SelectField label="Class"   value={form.classId}   onChange={(v) => { set("classId", v); set("subjectId", ""); }} options={classes.map((c) => ({ value: c.id, label: c.name }))} />
          <SelectField label="Subject *" value={form.subjectId} onChange={(v) => set("subjectId", v)} options={subjects.map((s) => ({ value: s.id, label: s.name }))} />
        </div>
        <div>
          <div style={s.fileLabel}>PDF File</div>
          <label style={s.fileZone}>
            <input type="file" accept=".pdf" style={{ display: "none" }} onChange={(e) => set("file", e.target.files[0])} />
            <span style={{ fontSize: 32 }}>📎</span>
            <span style={{ color: "#64748b", fontSize: 14, fontFamily: "'Sora',sans-serif" }}>
              {form.file ? form.file.name : "Click to select PDF file"}
            </span>
          </label>
        </div>
        <button type="submit" style={{ ...s.submitBtn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
          {loading ? "Uploading…" : "Upload Note →"}
        </button>
      </form>
    </div>
  );
}

/* ─────────────────────────────────────────────
   UPLOAD VIDEO — real API
───────────────────────────────────────────── */
function UploadVideo() {
  const [boards, setBoards]     = useState([]);
  const [classes, setClasses]   = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm]         = useState({ title: "", youtubeId: "", chapter: "", duration: "", boardId: "", classId: "", subjectId: "" });
  const [success, setSuccess]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => { getBoards().then(setBoards).catch(() => {}); }, []);
  useEffect(() => {
    if (form.boardId) getClasses(form.boardId).then(setClasses).catch(() => setClasses([]));
    else setClasses([]);
  }, [form.boardId]);
  useEffect(() => {
    if (form.boardId && form.classId) getSubjects(form.boardId, form.classId).then(setSubjects).catch(() => setSubjects([]));
    else setSubjects([]);
  }, [form.boardId, form.classId]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.youtubeId || !form.subjectId) { setError("Title, YouTube ID and Subject are required."); return; }
    setLoading(true); setError("");
    try {
      await uploadVideo({ title: form.title, youtubeId: form.youtubeId, chapter: form.chapter, duration: form.duration, subjectId: form.subjectId });
      setSuccess(true);
      setForm({ title: "", youtubeId: "", chapter: "", duration: "", boardId: "", classId: "", subjectId: "" });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add video");
    } finally { setLoading(false); }
  };

  return (
    <div style={s.formCard}>
      <div style={s.formTitle}>🎥 Add New Video</div>
      {success && <div style={s.successBanner}>✅ Video added successfully!</div>}
      {error   && <div style={s.errorBanner}>❌ {error}</div>}
      <form onSubmit={handleSubmit} style={s.form}>
        <div style={s.formGrid}>
          <Field label="Video Title *"    value={form.title}     onChange={(v) => set("title", v)}     placeholder="Chapter 1 – Introduction" />
          <Field label="YouTube Video ID *" value={form.youtubeId} onChange={(v) => set("youtubeId", v)} placeholder="dQw4w9WgXcQ" />
          <Field label="Chapter"          value={form.chapter}   onChange={(v) => set("chapter", v)}   placeholder="Chapter 1" />
          <Field label="Duration"         value={form.duration}  onChange={(v) => set("duration", v)}  placeholder="18:34" />
          <SelectField label="Board"   value={form.boardId}   onChange={(v) => { set("boardId", v); set("classId", ""); set("subjectId", ""); }} options={boards.map((b) => ({ value: b.id, label: b.name }))} />
          <SelectField label="Class"   value={form.classId}   onChange={(v) => { set("classId", v); set("subjectId", ""); }} options={classes.map((c) => ({ value: c.id, label: c.name }))} />
          <SelectField label="Subject *" value={form.subjectId} onChange={(v) => set("subjectId", v)} options={subjects.map((s) => ({ value: s.id, label: s.name }))} />
        </div>
        {form.youtubeId.trim() && (
          <div>
            <div style={s.fileLabel}>Preview</div>
            <div style={{ position: "relative", paddingTop: "35%", borderRadius: 10, overflow: "hidden", background: "#000" }}>
              <iframe style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                src={`https://www.youtube.com/embed/${form.youtubeId.trim()}?rel=0`}
                frameBorder="0" allowFullScreen title="preview" />
            </div>
          </div>
        )}
        <button type="submit" style={{ ...s.submitBtn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
          {loading ? "Adding…" : "Add Video →"}
        </button>
      </form>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MANAGE CONTENT — live list + real delete
───────────────────────────────────────────── */
function ManageContent() {
  const [view, setView]     = useState("notes");
  const [notes, setNotes]   = useState([]);
  const [videos, setVideos] = useState([]);
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]         = useState({ text: "", type: "ok" });

  const flash = (text, type = "ok") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "", type: "ok" }), 3000); };

  useEffect(() => { getBoards().then(setBoards).catch(() => {}); }, []);
  useEffect(() => {
    if (selectedBoard) getClasses(selectedBoard).then(setClasses).catch(() => setClasses([]));
    else setClasses([]);
    setSelectedClass(""); setSelectedSubject("");
  }, [selectedBoard]);
  useEffect(() => {
    if (selectedBoard && selectedClass) getSubjects(selectedBoard, selectedClass).then(setSubjects).catch(() => setSubjects([]));
    else setSubjects([]);
    setSelectedSubject("");
  }, [selectedBoard, selectedClass]);
  useEffect(() => {
    if (!selectedSubject) { setNotes([]); setVideos([]); return; }
    setLoading(true);
    Promise.all([getNotes(selectedSubject), getVideos(selectedSubject)])
      .then(([n, v]) => { setNotes(n); setVideos(v); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSubject]);

  const removeNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await deleteNote(id); setNotes((p) => p.filter((n) => n.id !== id)); flash("✅ Note deleted!"); }
    catch (e) { flash("❌ " + (e?.response?.data?.message || "Failed"), "err"); }
  };
  const removeVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await deleteVideo(id); setVideos((p) => p.filter((v) => v.id !== id)); flash("✅ Video deleted!"); }
    catch (e) { flash("❌ " + (e?.response?.data?.message || "Failed"), "err"); }
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <select style={s.filterSelect} value={selectedBoard} onChange={(e) => setSelectedBoard(e.target.value)}>
          <option value="">Select Board</option>
          {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select style={s.filterSelect} value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} disabled={!selectedBoard}>
          <option value="">Select Class</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={s.filterSelect} value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} disabled={!selectedClass}>
          <option value="">Select Subject</option>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}
        </select>
      </div>

      {msg.text && <div style={{ ...s.flashMsg, ...(msg.type === "err" ? s.flashErr : {}), marginBottom: 16 }}>{msg.text}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button style={{ ...s.tab, ...(view === "notes"  ? s.tabActive : {}) }} onClick={() => setView("notes")}>📄 Notes ({notes.length})</button>
        <button style={{ ...s.tab, ...(view === "videos" ? s.tabActive : {}) }} onClick={() => setView("videos")}>🎥 Videos ({videos.length})</button>
      </div>

      {loading && <div style={s.loadMsg}>Loading…</div>}
      {!selectedSubject && !loading && <div style={s.hintBox}>👆 Select a board, class and subject above to see content</div>}

      {view === "notes" && notes.map((n) => (
        <div key={n.id} style={{ ...s.manageRow, marginBottom: 10 }}>
          <span style={s.pdfBadge}>PDF</span>
          <div style={{ flex: 1 }}>
            <div style={s.manageTitle}>{n.title}</div>
            <div style={s.manageSub}>{n.description} · {n.fileSize}</div>
          </div>
          <button style={s.deleteBtn} onClick={() => removeNote(n.id)}>🗑 Delete</button>
        </div>
      ))}

      {view === "videos" && videos.map((v) => (
        <div key={v.id} style={{ ...s.manageRow, marginBottom: 10 }}>
          <div style={s.vidThumb}>▶</div>
          <div style={{ flex: 1 }}>
            <div style={s.manageTitle}>{v.title}</div>
            <div style={s.manageSub}>{v.chapter} · {v.duration}</div>
          </div>
          <button style={s.deleteBtn} onClick={() => removeVideo(v.id)}>🗑 Delete</button>
        </div>
      ))}
    </div>
  );
}

/* ─── Shared field components ─── */
function Field({ label, value, onChange, placeholder }) {
  return (
    <div style={s.fieldWrap}>
      <label style={s.fieldLabel}>{label}</label>
      <input style={s.fieldInput} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div style={s.fieldWrap}>
      <label style={s.fieldLabel}>{label}</label>
      <select style={s.fieldInput} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ─── Styles ─── */
const s = {
  page:       { maxWidth: 1000, margin: "0 auto", padding: "40px 20px 80px" },
  header:     { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 },
  greeting:   { fontSize: 22, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Sora',sans-serif" },
  role:       { fontSize: 12, color: "#64748b", fontFamily: "'Sora',sans-serif", marginTop: 4 },
  logoutBtn:  { background: "transparent", border: "1px solid #334155", color: "#94a3b8", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontFamily: "'Sora',sans-serif" },
  tabs:       { display: "flex", gap: 6, marginBottom: 28, flexWrap: "wrap" },
  tab:        { background: "transparent", border: "1px solid #334155", color: "#64748b", borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Sora',sans-serif" },
  tabActive:  { background: "#1e293b", color: "#fff", borderColor: "#475569" },
  statsGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 28 },
  statCard:   { background: "#1e293b", border: "1px solid #334155", borderRadius: 14, padding: "20px 16px", textAlign: "center" },
  statIcon:   { width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 12px" },
  statVal:    { fontSize: 28, fontWeight: 800, color: "#f1f5f9", fontFamily: "'Sora',sans-serif" },
  statLabel:  { fontSize: 12, color: "#64748b", fontFamily: "'Sora',sans-serif", marginTop: 4 },
  panel:      { background: "#1e293b", border: "1px solid #334155", borderRadius: 16, padding: "20px", display: "flex", flexDirection: "column", gap: 14 },
  panelHeader:{ display: "flex", alignItems: "center", justifyContent: "space-between" },
  panelTitle: { fontSize: 14, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Sora',sans-serif" },
  panelCount: { fontSize: 11, background: "#0f172a", color: "#64748b", borderRadius: 100, padding: "3px 10px", fontFamily: "'Sora',sans-serif" },
  flashMsg:   { background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 8, padding: "9px 14px", color: "#6ee7b7", fontSize: 13, fontFamily: "'Sora',sans-serif" },
  flashErr:   { background: "rgba(239,68,68,0.1)", borderColor: "rgba(239,68,68,0.3)", color: "#fca5a5" },
  hintBox:    { background: "#0f172a", border: "1px dashed #334155", borderRadius: 10, padding: "12px 16px", color: "#475569", fontSize: 13, fontFamily: "'Sora',sans-serif", textAlign: "center" },
  inlineForm: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" },
  inlineInput:{ background: "#0f172a", border: "1.5px solid #334155", borderRadius: 8, padding: "9px 12px", color: "#f1f5f9", fontSize: 13, fontFamily: "'Sora',sans-serif", outline: "none", flex: 1, minWidth: 110 },
  addBtn:     { background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap" },
  saveBtn:    { background: "#10b981", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Sora',sans-serif" },
  cancelBtn:  { background: "transparent", color: "#64748b", border: "1px solid #334155", borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer", fontFamily: "'Sora',sans-serif" },
  itemList:   { display: "flex", flexDirection: "column", gap: 6 },
  itemRow:    { display: "flex", alignItems: "center", gap: 12, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "10px 12px", cursor: "pointer" },
  itemRowActive:{ border: "1.5px solid #3b82f6", background: "#0d1f3c" },
  itemIcon:   { width: 34, height: 34, borderRadius: 8, background: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 },
  itemInfo:   { flex: 1, minWidth: 0 },
  itemName:   { fontSize: 13, fontWeight: 600, color: "#e2e8f0", fontFamily: "'Sora',sans-serif" },
  itemSub:    { fontSize: 11, color: "#475569", fontFamily: "'Sora',sans-serif", marginTop: 1 },
  itemActions:{ display: "flex", gap: 4, flexShrink: 0 },
  editBtn:    { background: "#1e293b", border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 13, cursor: "pointer" },
  delBtn:     { background: "rgba(239,68,68,0.1)", border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 13, cursor: "pointer" },
  noItems:    { fontSize: 13, color: "#475569", fontFamily: "'Sora',sans-serif", textAlign: "center", padding: "16px 0" },
  subjectForm:{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" },
  iconPicker: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  iconPickerLabel: { fontSize: 11, color: "#475569", fontFamily: "'Sora',sans-serif" },
  iconChip:   { background: "#0f172a", border: "1px solid #334155", borderRadius: 6, padding: "4px 8px", fontSize: 16, cursor: "pointer" },
  subjectGrid:{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10 },
  subjectCard:{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "14px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center" },
  subjectIcon:{ fontSize: 28 },
  subjectName:{ fontSize: 12, fontWeight: 600, color: "#cbd5e1", fontFamily: "'Sora',sans-serif" },
  formCard:   { background: "#1e293b", border: "1px solid #334155", borderRadius: 16, padding: "28px 24px" },
  formTitle:  { fontSize: 18, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Sora',sans-serif", marginBottom: 20 },
  successBanner:{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 10, padding: "12px 16px", color: "#6ee7b7", fontSize: 14, fontFamily: "'Sora',sans-serif", marginBottom: 20 },
  errorBanner:{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "12px 16px", color: "#fca5a5", fontSize: 14, fontFamily: "'Sora',sans-serif", marginBottom: 20 },
  form:       { display: "flex", flexDirection: "column", gap: 20 },
  formGrid:   { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16 },
  fieldWrap:  { display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: "#64748b", fontFamily: "'Sora',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" },
  fieldInput: { background: "#0f172a", border: "1.5px solid #334155", borderRadius: 10, padding: "11px 13px", color: "#f1f5f9", fontSize: 14, fontFamily: "'Sora',sans-serif", outline: "none", width: "100%" },
  fileLabel:  { fontSize: 11, fontWeight: 600, color: "#64748b", fontFamily: "'Sora',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 },
  fileZone:   { display: "flex", flexDirection: "column", alignItems: "center", gap: 10, background: "#0f172a", border: "2px dashed #334155", borderRadius: 12, padding: "28px 20px", cursor: "pointer" },
  submitBtn:  { background: "#3b82f6", color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'Sora',sans-serif" },
  manageRow:  { display: "flex", alignItems: "center", gap: 14, background: "#1e293b", border: "1px solid #334155", borderRadius: 12, padding: "14px 16px", flexWrap: "wrap" },
  manageTitle:{ fontSize: 14, fontWeight: 600, color: "#e2e8f0", fontFamily: "'Sora',sans-serif" },
  manageSub:  { fontSize: 12, color: "#64748b", fontFamily: "'Sora',sans-serif", marginTop: 2 },
  deleteBtn:  { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#fca5a5", borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap" },
  vidThumb:   { width: 44, height: 36, background: "#0f172a", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#3b82f6", fontSize: 16, flexShrink: 0 },
  filterSelect:{ background: "#1e293b", border: "1.5px solid #334155", borderRadius: 10, padding: "10px 14px", color: "#f1f5f9", fontSize: 13, fontFamily: "'Sora',sans-serif", outline: "none", minWidth: 160 },
  notAuth:    { textAlign: "center", padding: "100px 20px" },
  naTitle:    { fontSize: 22, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Sora',sans-serif", marginBottom: 8 },
  naSub:      { fontSize: 15, color: "#64748b", fontFamily: "'Sora',sans-serif", marginBottom: 24 },
  naBtn:      { background: "#3b82f6", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Sora',sans-serif" },
  loadMsg:    { color: "#64748b", fontFamily: "'Sora',sans-serif", fontSize: 14, padding: "20px 0" },
  errMsg:     { color: "#fca5a5", fontFamily: "'Sora',sans-serif", fontSize: 13, padding: "10px 0" },
  pdfBadge:   { background: "#ef4444", color: "#fff", fontSize: 9, fontWeight: 800, borderRadius: 6, padding: "4px 7px", fontFamily: "monospace", flexShrink: 0 },
  recentTitle:{ flex: 1, fontSize: 13, color: "#e2e8f0", fontFamily: "'Sora',sans-serif" },
};
